import "../assets/scss/WordTooltip.scss";

import { observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { Carousel } from "react-bootstrap";

import { IImageService } from "../services/image/IImageService";
import { ImageService } from "../services/image/ImageService";
import { DictionaryDefinition } from "../stores/dictionary/DictionaryDefinition";
import { DictionaryStore } from "../stores/dictionary/DictionaryStore";
import { ILoanWordsStore, WordFilter } from "../stores/word/LoanWordsStore";
import { Word } from "../stores/word/Word";

const MAX_ATTEMPTS = 3;
const RETRY_DELAY_IN_MILLIS = 1000;

export interface WordTooltipProps {
  dictionaryStore: DictionaryStore;
  filter: WordFilter;
  wordStore: ILoanWordsStore;
  imageService: IImageService;
}

@observer
export class WordTooltip extends React.Component<WordTooltipProps, undefined> {
  @observable private isSelectedFromCarousel = false;

  private retriesByWord: Map<string, number> = new Map<string, number>();

  componentDidMount() {
    this.handleNewProps(this.props);
  }

  componentWillReceiveProps(newProps: WordTooltipProps) {
    this.handleNewProps(newProps);
  }

  private handleNewProps(props: WordTooltipProps) {
    this.isSelectedFromCarousel = false;

    const words = this.getWordsForCarousel();

    let word = words[0];

    if (props.filter.originalWord) {
      const filteredWords = props.wordStore.getWords(props.filter);
      if (filteredWords.length !== 1) {
        throw new Error("expected to find 1 match to originalWord !");
      }

      word = filteredWords[0];
    }

    word = word || words[0];

    // only retrieve for the 1st word, else server can start rejecting the request
    // (and if local server, Chrome only allows about 6 connections unless http/2)
    this.retrieveDefinition(word);
    this.retrieveImageUrl(word);
  }

  private retrieveDefinition = (word: Word) => {
    if (word) {
      this.props.dictionaryStore.getDefinition(word, definition => {
        word.definition = definition;
      });
    }
  };

  private retrieveImageUrl = (word: Word) => {
    if (word) {
      this.props.imageService.getSmallImageUrlForWord(word);
    }
  };

  render() {
    return this.renderPopupContent();
  }

  private renderPopupContent = () => {
    const selectedWordText = this.props.filter.originalWord;

    const words = this.getWordsForCarousel();

    let selectedWord = null;
    if (selectedWordText) {
      selectedWord = words.filter(
        word => word.originalWord === selectedWordText
      )[0];
    }

    // make the selected word be first:
    // const wordsStartingWithSelected = [];
    let activeIndex = words.indexOf(selectedWord);
    if (activeIndex < 0) {
      activeIndex = 0;
    }

    const hasMultipleWords = words.length > 1;

    if (this.isSelectedFromCarousel) {
      activeIndex = undefined;
    }

    return (
      <Carousel
        interval={null}
        indicators={false}
        controls={hasMultipleWords}
        activeIndex={activeIndex}
        onSelect={this.onSelect}
        onClick={this.nullOnClick}
      >
        {words.map(this.renderCarouselItem)}
      </Carousel>
    );
  };

  private nullOnClick = (event: MouseEvent) => {
    // prevent 'any country' click handler:
    event.stopPropagation();
  };

  private renderCarouselItem = (word: Word): JSX.Element => {
    return (
      <Carousel.Item>
        <div className="wordTooltip-caption-img-container">
          <Carousel.Caption>
            <div className="wordTooltip-caption-text-container">
              <strong>{word.word}</strong> - ({word.originalWord},{" "}
              {word.originalLanguage})
            </div>
          </Carousel.Caption>
        </div>
        {this.renderText(word)}
      </Carousel.Item>
    );
  };

  private getWordsForCarousel = (): Word[] => {
    return this.props.wordStore.getWordsNotFilteredByWord(this.props.filter);
  };

  private renderText = (word: Word) => {
    return (
      <div className="wordTooltipTextDiv">
        <div className="wordTooltip-TextAndImageDiv">
          <div className="wordTooltip-TextDivNextToImage">
            {this.renderTextLine("loan word", word.word)}
            {this.renderTextLine("original language", word.originalLanguage)}
            {this.renderTextLine("original word", word.originalWord)}
            {this.renderTextLine("year", word.definition.date)}
            {this.renderTextLine("type", word.definition.wordType)}
          </div>
          {this.renderImage(word)}
        </div>
        {this.renderTextLine("definition", word.definition.definition)}
        {this.renderTextLine("etymology", word.definition.etymology)}
        {!!word.notes && this.renderTextLine("notes", word.notes)}
        {this.renderTextLine("audio", this.renderAudio(word.definition))}
        {this.renderTextLine(
          "more info",
          <a href={word.dictionaryUrl} target="_new_tab">
            online dictionary
          </a>
        )}
      </div>
    );
  };

  private renderImage = (word: Word): JSX.Element => {
    if (word.hasLocalImage) {
      return this.renderLocalImage(word);
    } else {
      return this.renderPublicImage(word);
    }
  };

  private renderLocalImage(word: Word): JSX.Element {
    // In production, the *local* images can be slow - so show a 'loading' image behind:
    return (
      <div className="word-tooltip-img-container">
        <div className="word-tooltip-image-div-fixed-width">
          <img
            className="word-tooltip-img-loading"
            src={ImageService.LOADING_IMAGE_URL}
          />
          <img
            className="word-tooltip-img"
            src={word.activeImageUrl}
            onError={(event: React.SyntheticEvent<HTMLImageElement>) =>
              this.onLoadImageError(event, word)
            }
          />
        </div>
      </div>
    );
  }

  // In production, images sometimes timeout (crappy server? or too long a roundtrip?)
  private onLoadImageError = (
    event: React.SyntheticEvent<HTMLImageElement>,
    word: Word
  ) => {
    const image = event.currentTarget;
    image.onerror = null;

    let attemptsMade = 0;
    if (this.retriesByWord.has(word.word)) {
      attemptsMade = this.retriesByWord.get(word.word);
    }

    if (attemptsMade >= MAX_ATTEMPTS) {
      console.warn(`[WordTooltip - image] giving up on '${word.word}'`);
      return;
    }

    // first retry is almost immediate (since browser has already waited some time)
    const delay = RETRY_DELAY_IN_MILLIS * Math.pow(3, attemptsMade);

    attemptsMade++;
    this.retriesByWord.set(word.word, attemptsMade);

    console.warn(
      `[WordTooltip - image] retrying '${word.word}' in ${delay}ms ...`
    );
    image.src = ImageService.LOADING_IMAGE_URL;

    setTimeout(() => {
      console.warn(`retrying image load for '${word.word}'`);
      image.src = word.activeImageUrl + "?" + new Date();
    }, delay);
  };

  private renderPublicImage(word: Word): JSX.Element {
    // public service images can be slow - so show a 'loading' image behind:
    return (
      <div className="word-tooltip-img-container">
        <div className="word-tooltip-image-div-fixed-width">
          <img
            className="word-tooltip-img-loading"
            src={ImageService.LOADING_IMAGE_URL}
          />
          <img className="word-tooltip-img" src={word.activeImageUrl} />
        </div>
      </div>
    );
  }

  private renderTextLine(title: string, other: string | JSX.Element) {
    const child =
      typeof other === "string" ? (
        <div className="wordTooltip-LineText">
          {this.renderMultiLineText(other)}
        </div>
      ) : (
        other
      );

    return (
      <div className="word-tooltip-line">
        <div className="wordTooltip-LineTitle">{title}:</div>
        {child}
      </div>
    );
  }

  private renderMultiLineText(text: string): JSX.Element[] {
    return text.split("\n").map(i => {
      return <div>{i}</div>;
    });
  }

  private renderAudio(definition: DictionaryDefinition): JSX.Element {
    const url = definition.audioWavUrl;

    if (!url) {
      return <>(not available)</>;
    }

    // set small width, to hide extra controls:
    return (
      <audio controls style={{ width: 100 }}>
        <source src={definition.audioWavUrl} type="audio/wav" />
      </audio>
    );
  }

  private onSelect = (index: number) => {
    this.isSelectedFromCarousel = true;

    const word = this.getWordsForCarousel()[index];

    this.retrieveDefinition(word);
    this.retrieveImageUrl(word);
  };
}



// WEBPACK FOOTER //
// ./components/WordTooltip.tsx